import React, { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ images, index, onClose, onNavigate }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const current = images[index];

  const go = useCallback(
    (dir) => {
      const n = (index + dir + images.length) % images.length;
      onNavigate(n);
    },
    [index, images.length, onNavigate]
  );

  useEffect(() => {
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "Tab") {
        const f = dialogRef.current?.querySelectorAll("button");
        if (!f || f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [go, onClose]);

  if (!current) return null;

  const btn =
    "inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/30";

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      data-testid="lightbox"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-stone-950/90 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button ref={closeRef} onClick={onClose} aria-label="Close photo viewer" data-testid="lightbox-close" className={`absolute top-4 right-4 ${btn}`}>
        <X className="h-6 w-6" aria-hidden="true" />
      </button>

      {images.length > 1 && (
        <button onClick={() => go(-1)} aria-label="Previous photo" data-testid="lightbox-prev" className={`absolute left-3 sm:left-6 ${btn}`}>
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </button>
      )}

      <figure className="flex max-h-[88vh] max-w-5xl flex-col items-center">
        <img src={current.src} alt={current.alt} className="max-h-[78vh] w-auto rounded-2xl object-contain" />
        <figcaption className="mt-3 max-w-2xl text-center text-sm text-stone-200">{current.alt}</figcaption>
      </figure>

      {images.length > 1 && (
        <button onClick={() => go(1)} aria-label="Next photo" data-testid="lightbox-next" className={`absolute right-3 sm:right-6 ${btn}`}>
          <ChevronRight className="h-6 w-6" aria-hidden="true" />
        </button>
      )}
    </div>,
    document.body
  );
};
